import React, { useEffect, useState } from "react";
import { Brain, Loader2 } from "lucide-react";
import { BottomSheet } from "@/components/BottomSheet";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

/**
 * KinlysEyesSheet — privacy toggles for what Kinly is allowed to see + do
 * for this user. Lifted out of the old Vault page so it lives under Settings.
 *
 * Backend: PATCH /families/{family_id}/members/{my_person_id} { ai_visibility }
 */
export function KinlysEyesSheet({ open, onClose }) {
  const { family, user } = useAuth();
  const [me, setMe] = useState(null);
  const [aiVis, setAiVis] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !family?.id || !user?.id) return;
    let cancelled = false;
    setLoading(true); setErr("");
    api
      .get(`/families/${family.id}/members`)
      .then(({ data }) => {
        if (cancelled) return;
        const m = (data || []).find((p) => p.user_id === user.id);
        if (m) {
          setMe(m);
          setAiVis(m.ai_visibility || {});
        }
      })
      .catch((e) => { if (!cancelled) setErr(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, family?.id, user?.id]);

  const save = async () => {
    if (!me || !family?.id) return;
    setSaving(true); setErr("");
    try {
      await api.patch(`/families/${family.id}/members/${me.id}`, {
        ai_visibility: aiVis,
      });
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2200);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <BottomSheet open={open} onClose={onClose} title="Kinly's eyes" fullHeight>
      <div className="space-y-4 pb-4" data-testid="kinlys-eyes-sheet">
        <article className="kinly-card flex items-start gap-3 border-primary/30 bg-primary/5">
          <div className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Brain className="w-4 h-4" />
          </div>
          <div>
            <p className="font-display text-[15px] font-medium leading-tight">
              What Kinly can see and do
            </p>
            <p className="text-[12px] text-muted-foreground mt-1 leading-snug">
              Turn off any area you'd rather Kinly stay out of. Changes take effect immediately.
            </p>
          </div>
        </article>

        {loading ? (
          <div className="kinly-card flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : err ? (
          <div className="kinly-card border-destructive/30 bg-destructive/5">
            <p className="text-sm text-destructive">{err}</p>
          </div>
        ) : !me ? (
          <div className="kinly-card">
            <p className="text-sm text-muted-foreground">Couldn't find your member profile.</p>
          </div>
        ) : (
          <article className="kinly-card divide-y divide-border/60" data-testid="kinlys-eyes-rows">
            <ToggleRow
              testid="ai-vis-read-chat"
              label="Read my chat"
              subtitle="Lets Kinly infer preferences from chat history."
              value={!!aiVis.can_read_chat}
              onChange={(v) => setAiVis({ ...aiVis, can_read_chat: v })}
            />
            <ToggleRow
              testid="ai-vis-summarise-mood"
              label="Notice my mood"
              subtitle="Lets Kinly read sentiment in chat."
              value={!!aiVis.can_summarise_mood}
              onChange={(v) => setAiVis({ ...aiVis, can_summarise_mood: v })}
            />
            <ToggleRow
              testid="ai-vis-coach"
              label="Coach me"
              subtitle="Lets Kinly nudge habits and streaks."
              value={!!aiVis.can_coach}
              onChange={(v) => setAiVis({ ...aiVis, can_coach: v })}
            />
            <ToggleRow
              testid="ai-vis-suggest-events"
              label="Suggest events for me"
              subtitle="Lets Kinly propose calendar items."
              value={!!aiVis.can_suggest_events}
              onChange={(v) => setAiVis({ ...aiVis, can_suggest_events: v })}
            />
          </article>
        )}

        {me && (
          <button
            type="button"
            data-testid="kinlys-eyes-save"
            onClick={save}
            disabled={saving}
            className="w-full h-12 rounded-xl bg-foreground text-background text-[14px] font-medium tap inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {savedAt ? "Saved" : saving ? "Saving…" : "Save"}
          </button>
        )}
      </div>
    </BottomSheet>
  );
}

function ToggleRow({ testid, label, subtitle, value, onChange }) {
  return (
    <div className="flex items-center justify-between py-3 gap-3" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <p className="text-[14px] text-foreground">{label}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={[
          "shrink-0 w-11 h-6 rounded-full relative transition-colors tap",
          value ? "bg-primary" : "bg-secondary",
        ].join(" ")}
      >
        <span
          className={[
            "absolute top-0.5 w-5 h-5 rounded-full bg-background shadow transition-transform",
            value ? "translate-x-5" : "translate-x-0.5",
          ].join(" ")}
        />
      </button>
    </div>
  );
}
